import { Sitter } from 'us-web-services';

export type TFunction = (key: string, options?: Record<string, unknown>) => string;

export interface SearchCardBadge {
  type?: string;
  label?: string;
  description?: string;
  url?: string;
  icon?: string;
}

export type ConnectionVariant =
  | 'singleFriend'
  | 'multipleFriends'
  | 'company'
  | 'school'
  | 'affiliation'
  | 'localGroup';

export interface SearchCardConnection {
  label: string;
  icon?: string;
  ariaLabel?: string;
  variant?: ConnectionVariant;
}

const MAX_BADGES_DEFAULT = 5;
const MAX_BADGES_WITH_COMPANY = 4;
const MAX_CONNECTION_ROW_SLOTS = 3;

const formatShortDate = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

export const getDistanceText = (t: TFunction, distance?: string | number): string => {
  if (distance == null || distance === '') return '';
  const value = typeof distance === 'string' ? parseFloat(distance) : distance;
  if (Number.isNaN(value)) return '';
  const rounded = Math.round(value * 10) / 10;
  return t('components.sitterSearchCard.distance', { count: rounded });
};

export const getSitterBadges = (t: TFunction, sitter: Sitter): SearchCardBadge[] => {
  const badges: SearchCardBadge[] = [];

  if (sitter.backgroundCheck && sitter.backgroundCheckDate) {
    badges.push({
      type: 'backgroundCheck',
      label: t('components.sitterSearchCard.badges.backgroundCheck', {
        date: formatShortDate(sitter.backgroundCheckDate),
      }),
    });
  }

  if (sitter.respondsQuicklyLabel) {
    badges.push({
      type: 'messageResponse',
      label: sitter.respondsQuicklyLabel,
    });
  }

  if (sitter.covidBadge?.consented) {
    badges.push({
      type: 'covidBadge',
      label: t('components.sitterSearchCard.badges.covidConsented'),
    });
  }

  if (sitter.cancellationRate?.label) {
    badges.push({
      type: 'reliable',
      label: sitter.cancellationRate.label,
    });
  }

  (sitter.badges ?? []).forEach((badge) => {
    if (badge.type !== 'backgroundCheck') {
      badges.push(badge);
    }
  });

  return badges;
};

export interface SelfBadge {
  label: string;
}

export const getSelfBadge = (t: TFunction, sitter: Sitter): SelfBadge | null => {
  const relationship = sitter.relationship;
  if (!relationship) return null;

  const directCount = relationship.connections?.direct?.length ?? 0;
  if (!directCount && !relationship.lastMessaged) return null;

  const parts: string[] = [];
  if (relationship.bookingCount) {
    parts.push(t('components.sitterSearchCard.selfBadge.completedBookings', {
      count: relationship.bookingCount,
    }));
  }
  if (relationship.lastMessaged) {
    parts.push(t('components.sitterSearchCard.selfBadge.lastMessaged', {
      date: formatShortDate(relationship.lastMessaged),
    }));
  }
  if (relationship.positiveReview) {
    parts.push(t('components.sitterSearchCard.selfBadge.positiveReview'));
  }
  return parts.length ? { label: parts.join(' ') } : null;
};

export const getIndirectConnections = (t: TFunction, sitter: Sitter): SearchCardConnection[] => {
  const indirect = sitter.relationship?.connections?.indirect ?? [];
  const count = indirect.length;

  if (count === 0) return [];

  if (count === 1) {
    const first = indirect[0];
    const name = first?.name;
    return [{
      label: name
        ? t('components.sitterSearchCard.connections.singleFriend', { name })
        : t('components.sitterSearchCard.connections.singleFriendFallback'),
      icon: first?.pictureUrl,
      variant: 'singleFriend',
    }];
  }

  return [{
    label: t('components.sitterSearchCard.connections.multipleFriends', { count }),
    icon: indirect[0]?.pictureUrl,
    variant: 'multipleFriends',
  }];
};

export const getGroupConnections = (
  t: TFunction,
  sitter: Sitter,
  indirectConnectionCount: number,
  hasSelfBadge: boolean,
): SearchCardConnection[] => {
  const connections = sitter.relationship?.connections;
  if (!connections) return [];

  const group: SearchCardConnection[] = [];
  const slotsRemaining = (): number =>
    MAX_CONNECTION_ROW_SLOTS - (indirectConnectionCount + group.length + (hasSelfBadge ? 1 : 0));

  const companyCount = connections.company?.length ?? 0;
  if (companyCount && slotsRemaining() > 0) {
    group.push({
      icon: connections.company?.[0]?.pictureUrl,
      label: t('components.sitterSearchCard.connections.company'),
      variant: 'company',
    });
  }

  const schoolCount = connections.school?.length ?? 0;
  if (schoolCount && slotsRemaining() > 0) {
    group.push({
      label: t('components.sitterSearchCard.connections.school', { count: schoolCount }),
      variant: 'school',
    });
  }

  const affiliationCount = connections.affiliation?.length ?? 0;
  if (affiliationCount && slotsRemaining() > 0) {
    group.push({
      label: t('components.sitterSearchCard.connections.affiliation', { count: affiliationCount }),
      variant: 'affiliation',
    });
  }

  const localGroupCount = connections.localGroup?.length ?? 0;
  if (localGroupCount && !schoolCount && !affiliationCount && slotsRemaining() > 0) {
    group.push({
      label: t('components.sitterSearchCard.connections.localGroup', { count: localGroupCount }),
      variant: 'localGroup',
    });
  }

  return group;
};

export const getBadgeDisplayCount = (
  sitter: Sitter,
  indirectConnectionCount: number,
  hasSelfBadge: boolean,
  totalBadges: number,
): { displayCount: number; overflowCount: number } => {
  const companyCount = sitter.relationship?.connections?.company?.length ?? 0;
  const maxBadgeCount = companyCount ? MAX_BADGES_WITH_COMPANY : MAX_BADGES_DEFAULT;
  const adjusted = maxBadgeCount - indirectConnectionCount * 2 - (hasSelfBadge ? 1 : 0);
  const displayCount = Math.max(adjusted, 0);
  const overflowCount = Math.max(totalBadges - displayCount, 0);
  return { displayCount, overflowCount };
};

const NO_EXPERIENCE_SHORTLABEL = ['0', ''];

export const getExperienceString = (t: TFunction, sitter: Sitter): string => {
  if (!sitter.experiences) return '';
  const parts: string[] = [];
  const stage = (
    bucket: { shortLabel?: string } | undefined,
    key: 'experienceInfants' | 'experienceToddlers' | 'experienceSchool',
  ): void => {
    const shortLabel = bucket?.shortLabel;
    if (!shortLabel || NO_EXPERIENCE_SHORTLABEL.includes(shortLabel)) return;
    const count = shortLabel === '<1' ? 1 : 2;
    parts.push(t(`components.sitterSearchCard.highlight.${key}`, { shortLabel, count }));
  };
  stage(sitter.experiences.infant, 'experienceInfants');
  stage(sitter.experiences.toddler, 'experienceToddlers');
  stage(sitter.experiences.school, 'experienceSchool');
  return parts.length
    ? `${t('components.sitterSearchCard.highlight.experiencePrefix')}${parts.join(', ')}`
    : '';
};

export const getHighlightText = (t: TFunction, sitter: Sitter): string => {
  const repeatFamilies = sitter.repeatFamilies ?? 0;
  if (repeatFamilies > 0) {
    return t('components.sitterSearchCard.highlight.repeatFamilies', { count: repeatFamilies });
  }
  return getExperienceString(t, sitter);
};

export const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.substring(0, max)}...` : text;
